import { SettingSystemType } from "@/utilities/enums/SystemSetting";
import SystemSettingRepository from "@/utilities/repositories/SystemSetting";
import { IKillSwitchData, ITopBarData } from "@/utilities/types/SystemSetting";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button, Input, Switch, Text } from "rizzui";
import { useForm, Controller } from "react-hook-form";
import { useModal } from "@/app/shared/modal-views/use-modal";
import { PiGearBold } from "react-icons/pi";

type KillSwitchFormValues = {
  enabled: boolean;
  message: string;
};

type TopBarFormValues = {
  enabled: boolean;
  message: string;
};

export default function SystemSettingsModal() {
  const { closeModal } = useModal();

  const [submittingKillSwitch, setSubmittingKillSwitch] = useState(false);
  const [submittingTopBar, setSubmittingTopBar] = useState(false);

  const [killSwitchId, setKillSwitchId] = useState("");
  const [topBarId, setTopBarId] = useState("");

  const killSwitchForm = useForm<KillSwitchFormValues>({
    defaultValues: {
      enabled: false,
      message: "",
    },
  });

  const topBarForm = useForm<TopBarFormValues>({
    defaultValues: {
      enabled: false,
      message: "",
    },
  });

  const fetchData = useCallback(async () => {
    // Fetch Kill Switch
    try {
      const killSwitchResponse = await SystemSettingRepository.getKillSwitch();
      if (killSwitchResponse?.data) {
        const killSwitchData = killSwitchResponse.data.data as IKillSwitchData;
        killSwitchForm.reset({
          enabled: killSwitchResponse.data.status === "active",
          message: killSwitchData.reason,
        });
        setKillSwitchId(killSwitchResponse.data.id);
      }
    } catch (error) {
      console.error("Failed to fetch kill switch:", error);
    }

    // Fetch Top Bar
    try {
      const topBarResponse =
        await SystemSettingRepository.getAgentDashboardTopBar();
      if (topBarResponse?.data) {
        const topBarData = topBarResponse.data.data as ITopBarData;
        topBarForm.reset({
          enabled: topBarResponse.data.status === "active",
          message: topBarData.message,
        });
        setTopBarId(topBarResponse.data.id);
      }
    } catch (error) {
      console.error("Failed to fetch top bar:", error);
      // Top bar setting might not exist yet, that's okay
    }
  }, [killSwitchForm, topBarForm]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitKillSwitch = useCallback(
    async (data: KillSwitchFormValues) => {
      if (!killSwitchId) {
        toast.error("Kill switch setting not found. Please try again later.");
        return;
      }
      try {
        setSubmittingKillSwitch(true);
        await SystemSettingRepository.startKillSwitch(killSwitchId, {
          type: SettingSystemType.KILL_SWITCH,
          status: data.enabled ? "active" : "inactive",
          data: { reason: data.message },
        });
        toast.success("Kill switch updated");
        await fetchData();
      } catch (error) {
        toast.error("Failed to update kill switch");
        console.error(error);
      } finally {
        setSubmittingKillSwitch(false);
      }
    },
    [killSwitchId, submittingKillSwitch, killSwitchForm],
  );

  const onSubmitTopBar = useCallback(
    async (data: TopBarFormValues) => {
      if (!topBarId) {
        toast.error("Top bar setting not found. Please try again later.");
        return;
      }
      try {
        setSubmittingTopBar(true);
        await SystemSettingRepository.updateAgentDashboardTopBar(topBarId, {
          type: SettingSystemType.AGENT_DASHBOARD_TOP_BAR,
          status: data.enabled ? "active" : "inactive",
          data: { message: data.message },
        });
        toast.success("Top bar message updated");
        await fetchData();
      } catch (error) {
        toast.error("Failed to update top bar message");
        console.error(error);
      } finally {
        setSubmittingTopBar(false);
      }
    },
    [topBarId, submittingTopBar, topBarForm],
  );

  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-6">
        <PiGearBold className="h-6 w-6" />
        <h2 className="text-xl font-bold">Global Settings</h2>
      </div>

      <div className="space-y-8">
        {/* Kill Switch Section */}
        <div className="border-b pb-6">
          <Text className="font-bold text-lg mb-4">Kill Switch</Text>
          <form
            className="flex flex-col gap-4"
            onSubmit={killSwitchForm.handleSubmit(onSubmitKillSwitch)}
          >
            <div className="flex items-center gap-4">
              <Controller
                name="enabled"
                control={killSwitchForm.control}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <Switch
                      label="Enabled"
                      checked={field.value}
                      onChange={() => field.onChange(!field.value)}
                    />
                  </div>
                )}
              />
            </div>
            <Controller
              name="message"
              control={killSwitchForm.control}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Maintenance Message"
                  className="w-full"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter maintenance message..."
                />
              )}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={
                  !killSwitchForm.formState.isDirty || submittingKillSwitch
                }
                isLoading={submittingKillSwitch}
              >
                Save Kill Switch
              </Button>
            </div>
          </form>
        </div>

        {/* Agent Dashboard Top Bar Section */}
        <div>
          <Text className="font-bold text-lg mb-4">
            Agent Dashboard Top Bar
          </Text>
          <form
            className="flex flex-col gap-4"
            onSubmit={topBarForm.handleSubmit(onSubmitTopBar)}
          >
            <div className="flex items-center gap-4">
              <Controller
                name="enabled"
                control={topBarForm.control}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <Switch
                      label="Enabled"
                      checked={field.value}
                      onChange={() => field.onChange(!field.value)}
                    />
                  </div>
                )}
              />
            </div>
            <Controller
              name="message"
              control={topBarForm.control}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Top Bar Message"
                  className="w-full"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter message to display on Agent Dashboard..."
                />
              )}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!topBarForm.formState.isDirty || submittingTopBar}
                isLoading={submittingTopBar}
              >
                Save Top Bar
              </Button>
            </div>
          </form>
        </div>
      </div>

      <div className="flex justify-end mt-6 pt-4 border-t">
        <Button variant="outline" onClick={closeModal}>
          Close
        </Button>
      </div>
    </div>
  );
}
